import { useState, useMemo } from 'react'
import { useContacts } from './hooks/useContacts'
import { useMembers } from '../../hooks/useMembers'
import { useAuth } from '../../hooks/useAuth'
import { ContactForm } from './components/ContactForm'
import { ContactDetail } from './components/ContactDetail'
import { SearchBar } from '../../components/ui/SearchBar'
import { SkeletonList } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/ui/EmptyState'
import { getInitials } from '../../lib/utils'
import { cn } from '../../lib/utils'

const RELATIONSHIPS = ['doctor','dentist','coach','teacher','therapist','emergency','family','friend','other']

const CONTACT_AVATAR_SIZES = { 8: 'w-8 h-8', 10: 'w-10 h-10', 12: 'w-12 h-12' }

const ContactAvatar = ({ contact, photoUrl, size = 10 }) => {
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ')
  return (
    <div className={cn(CONTACT_AVATAR_SIZES[size] ?? 'w-10 h-10', 'rounded-full flex items-center justify-center overflow-hidden bg-primary-light flex-shrink-0')}>
      {photoUrl ? (
        <img src={photoUrl} alt={fullName} className="w-full h-full object-cover" />
      ) : (
        <span className="text-primary font-bold text-sm">{getInitials(fullName)}</span>
      )}
    </div>
  )
}

const ContactCard = ({ contact, onClick }) => {
  const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(' ')

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left flex items-center gap-3 p-4 rounded-xl border transition-colors hover:border-primary/30',
        contact.is_emergency ? 'bg-rose-50 border-rose-200' : 'bg-surface border-border',
      )}
    >
      <ContactAvatar contact={contact} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-text-primary">{fullName}</span>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary-light text-primary capitalize">
            {contact.relationship}
          </span>
          {contact.is_emergency && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">Emergency</span>
          )}
        </div>
        {contact.organization && (
          <p className="text-xs text-text-muted truncate mt-0.5">{contact.organization}</p>
        )}
        {contact.phone_primary && (
          <p className="text-xs text-text-secondary mt-0.5">{contact.phone_primary}</p>
        )}
      </div>
    </button>
  )
}

export const ContactsPage = () => {
  const { isLimited } = useAuth()
  const { data: contacts = [], isLoading, create, update, remove } = useContacts()
  const { data: members = [] }  = useMembers()

  const [search,      setSearch]      = useState('')
  const [relFilter,   setRelFilter]   = useState('all')
  const [memberFilter,setMemberFilter]= useState(null)
  const [showForm,    setShowForm]    = useState(false)
  const [editing,     setEditing]     = useState(null)
  const [viewing,     setViewing]     = useState(null)

  const realMembers = members.filter((m) => !m.isShared)

  const filtered = useMemo(() => {
    let list = contacts
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((c) =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
        c.organization?.toLowerCase().includes(q) ||
        c.phone_primary?.includes(q)
      )
    }
    if (relFilter !== 'all') {
      if (relFilter === 'emergency') {
        list = list.filter((c) => c.is_emergency)
      } else {
        list = list.filter((c) => c.relationship === relFilter)
      }
    }
    if (memberFilter) {
      list = list.filter((c) => (c.member_ids ?? []).includes(memberFilter))
    }
    return list
  }, [contacts, search, relFilter, memberFilter])

  const emergency   = contacts.filter((c) => c.is_emergency)
  const nonEmergency = filtered.filter((c) => !c.is_emergency || relFilter === 'emergency')
  const showEmergencySection = !search.trim() && relFilter === 'all' && !memberFilter && emergency.length > 0

  const handleSave = async (payload) => {
    if (editing) {
      await update.mutateAsync({ id: editing.id, ...payload })
    } else {
      await create.mutateAsync(payload)
    }
  }

  const handleEdit = (contact) => {
    setViewing(null)
    setEditing(contact)
    setShowForm(true)
  }

  if (isLoading) return <div className="px-4 py-5"><SkeletonList count={5} /></div>

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-text-primary">Contacts</h1>
        {!isLimited && (
          <button
            onClick={() => { setEditing(null); setShowForm(true) }}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-primary/90"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add
          </button>
        )}
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search contacts…" className="mb-4" />

      {/* Relationship filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-3 scrollbar-hide">
        {['all', 'emergency', ...RELATIONSHIPS].map((r) => (
          <button
            key={r}
            onClick={() => setRelFilter(r)}
            className={cn(
              'flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors capitalize',
              relFilter === r
                ? 'bg-primary text-white border-primary'
                : 'bg-background text-text-secondary border-border hover:border-primary/50'
            )}
          >
            {r === 'all' ? 'All' : r}
          </button>
        ))}
      </div>

      {/* Member filter chips */}
      {realMembers.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4 scrollbar-hide">
          {realMembers.map((m) => {
            const uid  = m.user_id
            const name = m.profiles?.display_name ?? 'Member'
            return (
              <button
                key={uid}
                onClick={() => setMemberFilter(memberFilter === uid ? null : uid)}
                className={cn(
                  'flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors',
                  memberFilter === uid
                    ? 'bg-primary text-white border-primary'
                    : 'bg-background text-text-secondary border-border hover:border-primary/50'
                )}
              >
                {name}
              </button>
            )
          })}
        </div>
      )}

      {contacts.length === 0 ? (
        <EmptyState
          type="contacts"
          title="No contacts yet"
          description="Save doctors, coaches, emergency contacts and more for your household."
        />
      ) : (
        <>
          {/* Emergency section — pinned at top when no filter active */}
          {showEmergencySection && (
            <div className="mb-5">
              <h2 className="text-xs font-semibold text-rose-600 uppercase tracking-wide mb-2">
                Emergency Contacts
              </h2>
              <div className="space-y-2">
                {emergency.map((c) => (
                  <ContactCard key={c.id} contact={c} onClick={() => setViewing(c)} />
                ))}
              </div>
            </div>
          )}

          {/* Full list */}
          {(showEmergencySection ? filtered.filter((c) => !c.is_emergency) : filtered).length > 0 && (
            <div className="space-y-2">
              {(showEmergencySection ? filtered.filter((c) => !c.is_emergency) : filtered).map((c) => (
                <ContactCard key={c.id} contact={c} onClick={() => setViewing(c)} />
              ))}
            </div>
          )}

          {filtered.length === 0 && (
            <p className="text-sm text-text-muted text-center py-8">No contacts match your filters.</p>
          )}
        </>
      )}

      {showForm && (
        <ContactForm
          contact={editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null) }}
        />
      )}

      {viewing && (
        <ContactDetail
          contact={viewing}
          onEdit={handleEdit}
          onDelete={remove.mutateAsync}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  )
}
